import { Application } from "./interface";
import FeatureFlagService from "./FeatureFlagService";
import { BaseWebSocketService, IConfig } from "./HubConnection";
import * as signalR from "@microsoft/signalr";
import { authService } from "../authorizationService";

/**
 * Service for managing feature flag updates via WebSocket using SignalR.
 * This service extends the BaseWebSocketService to include specific functionality
 * for handling feature flag updates, ensuring that feature flags can be tracked,
 * fetched directly when necessary, and updated in real-time via SignalR.
 */
export class FeatureFlagWebSocketService extends BaseWebSocketService<Application> {
    /**
     * Constructs a new instance of the FeatureFlagWebSocketService.
     * Sets up a listener for feature flag updates from the SignalR server.
     * @param {IConfig} configuration Configuration settings for the WebSocket service,
     * including the URL for the SignalR hub and environment flags.
     */
    constructor(configuration: IConfig) {
        super(configuration);
        // Listen for real-time feature flag updates from the server
        this.connection.on("SendFeatureFlags", this.onReceive.bind(this));
    }

    /**
     * Callback for handling received feature flag updates.
     * This method logs and processes the feature flag data received from the server.
     * @param {FeatureFlag} data The feature flag data received from the server.
     */
    public onReceive(data: Application): void {
        console.log("Received feature flags", data);
        data && data.featureFlags && FeatureFlagService.setFeatureFlagsForApplication(data.applicationId, data.featureFlags);
    }

    /**
     * Fetches a feature flag directly from the API as a fallback mechanism.
     * This method is used when SignalR is unable to fetch the feature flag, either
     * due to connection issues or if the real-time update fails.
     * @param {string} applicationId The ID of the application for which to fetch the feature flag.
     * @returns {Promise<Application>} A promise that resolves with the feature flag data.
     */
    private async fetchFeatureFlagDirectly(applicationId: string): Promise<Application> {
        const accessToken = await authService.getAccessToken();
        const url = `${this.configuration.apiBaseUrl}/applications/${applicationId}`;
        try {
            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                },
            });
            if (!response.ok) {
                throw new Error('Failed to fetch feature flag from API');
            }
            const data: Application = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching feature flag directly:', error);
            throw error;
        }
    }

    /**
     * Tracks a feature flag for a specific application. This method first attempts to
     * join a SignalR group corresponding to the application ID. If joining the group
     * fails, or if the WebSocket connection isn't established, it falls back to fetching
     * the feature flag directly from the API.
     * @param {number} applicationId The ID of the application to track.
     * @returns {Promise<Application|undefined>} A promise that resolves with the feature flag,
     * or undefined if the operation fails.
     */
    public async trackFeatureFlag(applicationId: number): Promise<Application | undefined> {
        if (this.connection.state === signalR.HubConnectionState.Connected) {
            try {
                // Attempt to join the SignalR group and await feature flag updates
                return await this.joinGroup(applicationId.toString());
            } catch (error) {
                console.log(`SignalR group join failed, fetching feature flag directly for applicationId ${applicationId}:`, error);
                // Fall back to direct API fetch if joining the group fails
                return await this.fetchFeatureFlagDirectly(applicationId.toString());
            }
        } else {
            console.log(`Connection not established. Queuing group ${applicationId} to join and will fetch directly as fallback.`);
            this.joinGroupQueue.push(applicationId.toString()); // Queue the group join request
            // Directly fetch the feature flag as a fallback while waiting for the connection to establish
            return this.fetchFeatureFlagDirectly(applicationId.toString());
        }
    }

    /**
     * Attempts to join a group to receive updates for a specific feature flag. Overrides
     * the BaseWebSocketService.joinGroup method to handle the 'GroupJoined' event,
     * which confirms the group join and potentially provides initial feature flag data.
     * @param {string} group The name of the group to join, typically corresponding to an application ID.
     * @returns {Promise<Application>} A promise that resolves with the feature flag data
     * received upon successfully joining the group.
     */
    public async joinGroup(group: string): Promise<Application> {
        console.log(`Joining group ${group}`);
        return new Promise<Application>((resolve, reject) => {
            // Define a one-time handler for receiving the feature flag data
            const handler = (data: Application) => {
                console.log(`Received feature flag data for group ${group}`, data);
                this.connection.off("GroupJoined", handler); // Unsubscribe to ensure the handler is called at most once
                resolve(data);
            };

            // Subscribe to the 'GroupJoined' event to receive the feature flag data
            this.connection.on("GroupJoined", handler);

            // Attempt to join the group through the base class method
            super.joinGroup(group).catch(error => {
                // If joining the group fails, unsubscribe and reject the promise
                this.connection.off("GroupJoined", handler);
                reject(error);
            });
        });
    }
}
